import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  Select,
  Toast,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowSync20Regular,
  Checkmark20Regular,
  Delete20Regular,
  Dismiss20Regular,
  Dismiss24Regular,
  Edit20Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import styles from "./Settup.module.css";

/* ------------------------------------------------------------------ */
/* Accounting setup screens.                                           */
/* Talks to the accounting service (same endpoints the Accounting      */
/* screen uses) with the signed-in user's JWT.                         */
/* ------------------------------------------------------------------ */

export type AccountingView =
  | "Accounts"
  | "AccountGroups"
  | "Banks"
  | "Taxes"
  | "Customers"
  | "Vendors"
  | "AssetCategories";

interface SettupAccountingProps {
  view: AccountingView;
  token: string;
  /** Accounting service address. */
  baseUrl?: string;
}

type Val = string | number;
type Row = { id: string; status?: string; [k: string]: any };
type Opt = { v: string; l: string };
type Intent = "success" | "error" | "warning";

/* ---------- http ---------- */

class ApiError extends Error {}

const makeHttp = (baseUrl: string, token: string) => {
  const qs = (q?: Record<string, string | number | boolean>) =>
    q
      ? "?" +
        Object.entries(q)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";
  return async (method: string, path: string, body?: unknown, q?: Record<string, string | number | boolean>): Promise<any> => {
    const headers: Record<string, string> = { Accept: "application/json", Authorization: `Bearer ${token}` };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    let res: Response;
    try {
      res = await fetch(`${baseUrl.replace(/\/$/, "")}${path}${qs(q)}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError("Can't reach the accounting service. Check your connection.");
    }
    if (res.status === 204) return undefined;
    const text = await res.text();
    let json: any = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    if (!res.ok) {
      let msg = json?.detail || json?.title || json?.message || (typeof json === "string" ? json : "");
      if (json?.errors && typeof json.errors === "object") {
        const first = Object.values(json.errors as Record<string, string[]>)[0];
        if (Array.isArray(first) && first[0]) msg = first[0];
      }
      if (!msg) {
        msg =
          res.status === 401
            ? "You are signed out. Sign in again."
            : res.status === 403
            ? "You don't have permission to do that."
            : `Request failed (${res.status}).`;
      }
      throw new ApiError(String(msg));
    }
    return json;
  };
};
type Http = ReturnType<typeof makeHttp>;

/* ---------- small helpers ---------- */

const items = (p: any): any[] => (Array.isArray(p) ? p : Array.isArray(p?.items) ? p.items : Array.isArray(p?.$values) ? p.$values : []);
const n = (v: any): number => (typeof v === "number" ? v : Number(v) || 0);
const s = (v: any): string => (v === null || v === undefined ? "" : String(v));
const PAGE = { page: 1, pageSize: 500 };
const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : "Something went wrong");
const money = (v: number) => {
  try {
    return new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(Math.round(v) || 0);
  } catch {
    return `NGN ${Math.round(v).toLocaleString()}`;
  }
};
const pad4 = (k: number) => String(k).padStart(4, "0");
const nextCode = (prefix: string, list: Row[]) =>
  `${prefix}${pad4(list.reduce((m, r) => Math.max(m, Number(r.id.slice(prefix.length)) || 0), 0) + 1)}`;
const rawOf = (r: Row | null) => (r?.raw ?? {}) as Record<string, any>;

const TONE: Record<string, string> = {
  Active: "green",
  Inactive: "grey",
  Asset: "blue",
  Equity: "blue",
  Liability: "amber",
  "Cost of sales": "amber",
  Revenue: "green",
  Expense: "red",
  VAT: "blue",
  WHT: "amber",
  CIT: "red",
  PAYE: "green",
};
const Pill: React.FC<{ text: string }> = ({ text }) => (
  <span className={`${styles.accPill} ${styles["accTone_" + (TONE[text] || "grey")]}`}>{text}</span>
);

/* ---------- entity model ---------- */

interface Fld {
  key: string;
  label: string;
  kind: "text" | "number" | "select";
  required?: boolean;
  full?: boolean;
  /** cannot be changed once the record exists */
  lock?: boolean;
  hint?: string;
  options?: (ctx: Ctx) => Opt[];
}
interface Col {
  key: string;
  label: string;
  kind?: "mono" | "money" | "pct" | "status" | "num";
  right?: boolean;
  get?: (r: Row, ctx: Ctx) => Val;
}
interface Tile {
  label: string;
  value: string;
  note?: string;
}
interface Ctx {
  accounts: Row[];
  groups: Row[];
}
interface Entity {
  title: string;
  subtitle: string;
  noun: string;
  lookups: ("accounts" | "groups")[];
  load: (h: Http, ctx: Ctx) => Promise<Row[]>;
  cols: Col[];
  fields: Fld[];
  filterKey?: string;
  filterLabel?: string;
  tiles: (list: Row[]) => Tile[];
  defaults?: () => Record<string, Val>;
  toForm?: (r: Row, ctx: Ctx) => Record<string, Val>;
  validate?: (v: Record<string, Val>, ctx: Ctx, ex: Row | null, list: Row[]) => string | null;
  save?: (h: Http, v: Record<string, Val>, ctx: Ctx, ex: Row | null, list: Row[]) => Promise<string>;
  remove?: (h: Http, r: Row) => Promise<string>;
  toggle?: (h: Http, r: Row, on: boolean) => Promise<string>;
}

const accountOpts =
  (...types: string[]) =>
  (ctx: Ctx): Opt[] =>
    ctx.accounts
      .filter((a) => (types.length === 0 || types.includes(s(a.type))) && a.status === "Active")
      .map((a) => ({ v: a.id, l: `${a.id} · ${s(a.name)}` }));
const accountId = (ctx: Ctx, code: Val | undefined) => n(ctx.accounts.find((a) => a.id === s(code))?._id);
const TERMS = ["Due on receipt", "Net 15", "Net 30", "Net 45"];

const partyEntity = (kind: "customer" | "vendor"): Entity => {
  const isC = kind === "customer";
  const base = isC ? "/api/receivables/customers" : "/api/payables/vendors";
  const prefix = isC ? "CU-" : "VN-";
  const ctrlDefault = isC ? "1200" : "2000";
  return {
    title: isC ? "Customers" : "Vendors",
    subtitle: isC ? "The people and companies you invoice, and the terms they pay on." : "The suppliers you buy from, and the terms you pay them on.",
    noun: kind,
    lookups: ["accounts"],
    load: async (h) => {
      const [list, bal] = await Promise.all([
        h("GET", base, undefined, { ...PAGE, includeInactive: true }),
        h("GET", `/api/dashboard/lookups/${isC ? "customers" : "vendors"}`).catch(() => [] as any[]),
      ]);
      const owing = new Map<number, number>(items(bal).map((b: any) => [n(b.partyId), n(b.outstanding)]));
      return items(list).map((c) => {
        const idKey = isC ? "customerId" : "vendorId";
        return {
          id: s(isC ? c.customerCode : c.vendorCode),
          _id: n(c[idKey]),
          name: s(isC ? c.customerName : c.vendorName),
          email: s(c.email),
          phone: s(c.phone),
          terms: s(c.paymentTerms),
          balance: owing.get(n(c[idKey])) ?? 0,
          status: c.isActive ? "Active" : "Inactive",
          raw: c,
        } as Row;
      });
    },
    cols: [
      { key: "id", label: isC ? "Customer" : "Vendor", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "terms", label: "Terms" },
      { key: "balance", label: isC ? "Outstanding" : "We owe", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "email", label: "Email", kind: "text" },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "terms", label: "Payment terms", kind: "select", required: true, options: () => TERMS.map((t) => ({ v: t, l: t })) },
    ],
    filterKey: "status",
    filterLabel: "All statuses",
    tiles: (list) => [
      { label: isC ? "Customers" : "Vendors", value: String(list.length), note: `${list.filter((r) => r.status === "Active").length} active` },
      { label: isC ? "Owed to you" : "You owe", value: money(list.reduce((t, r) => t + n(r.balance), 0)), note: "Across all accounts" },
      { label: "Inactive", value: String(list.filter((r) => r.status === "Inactive").length), note: "Hidden from new documents" },
    ],
    defaults: () => ({ terms: "Net 30" }),
    toForm: (r) => ({ name: s(r.name), email: s(r.email), phone: s(r.phone), terms: s(r.terms) || "Net 30" }),
    save: async (h, v, ctx, ex, list) => {
      const old = rawOf(ex);
      const firstRaw = list.find((r) => rawOf(r).controlAccountId)?.raw ?? {};
      const code = ex ? ex.id : nextCode(prefix, list);
      const control = n(old.controlAccountId) || n(firstRaw.controlAccountId) || accountId(ctx, ctrlDefault);
      if (!control) throw new ApiError(`No control account found. Create account ${ctrlDefault} first.`);
      const body: Record<string, any> = isC
        ? {
            customerCode: code, customerName: s(v.name), controlAccountId: control, paymentTerms: s(v.terms) || null,
            currencyCode: old.currencyCode ?? null, taxId: old.taxId ?? null, contactName: old.contactName ?? null,
            email: s(v.email) || null, phone: s(v.phone) || null, address: old.address ?? null, creditLimit: n(old.creditLimit),
          }
        : {
            vendorCode: code, vendorName: s(v.name), controlAccountId: control, paymentTerms: s(v.terms) || null,
            currencyCode: old.currencyCode ?? null, taxId: old.taxId ?? null, contactName: old.contactName ?? null,
            email: s(v.email) || null, phone: s(v.phone) || null, address: old.address ?? null,
            bankName: old.bankName ?? null, bankAccount: old.bankAccount ?? null, bankSortCode: old.bankSortCode ?? null,
          };
      if (ex) await h("PUT", `${base}/${ex._id}`, body);
      else await h("POST", base, body);
      return `${code} ${ex ? "saved" : "created"}`;
    },
    toggle: async (h, r, on) => {
      await h("POST", `${base}/${r._id}/${on ? "activate" : "deactivate"}`, {});
      return `${s(r.name)} ${on ? "activated" : "deactivated"}`;
    },
  };
};

const ENTITIES: Record<AccountingView, Entity> = {
  Accounts: {
    title: "Chart of accounts",
    subtitle: "Every account the ledger can post to. Codes are permanent once created.",
    noun: "account",
    lookups: ["groups"],
    load: async (h) =>
      items(await h("GET", "/api/dashboard/ledger/accounts")).map(
        (a) =>
          ({
            id: s(a.code), _id: n(a.accountId), name: s(a.name), type: s(a.type), group: s(a.group), groupId: n(a.groupId),
            balance: n(a.balance), isBank: a.isBank ? 1 : 0, status: a.isActive ? "Active" : "Inactive", raw: a,
          } as Row)
      ),
    cols: [
      { key: "id", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type", kind: "status" },
      { key: "group", label: "Group" },
      { key: "balance", label: "Balance today", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "code", label: "Account code", kind: "text", required: true, lock: true, hint: "Cannot be changed later" },
      { key: "name", label: "Account name", kind: "text", required: true },
      { key: "group", label: "Account group", kind: "select", required: true, full: true, options: (ctx) => ctx.groups.map((g) => ({ v: g.id, l: s(g.label) })) },
    ],
    filterKey: "type",
    filterLabel: "All types",
    tiles: (list) => [
      { label: "Accounts", value: String(list.length), note: `${list.filter((r) => r.status === "Active").length} active` },
      { label: "Bank / cash", value: String(list.filter((r) => n(r.isBank) === 1).length), note: "Linked to bank accounts" },
      { label: "Revenue", value: String(list.filter((r) => r.type === "Revenue").length), note: "Income accounts" },
      { label: "Expense", value: String(list.filter((r) => r.type === "Expense" || r.type === "Cost of sales").length), note: "Cost accounts" },
    ],
    defaults: () => ({}),
    toForm: (r) => ({ code: r.id, name: s(r.name), group: s(r.groupId) }),
    validate: (v, _c, ex, list) => (!ex && list.some((a) => a.id === s(v.code).trim()) ? `Account ${s(v.code)} already exists.` : null),
    save: async (h, v, _c, ex) => {
      if (ex) {
        await h("PUT", `/api/ledger/accounts/${ex._id}`, {
          accountName: s(v.name), groupId: n(v.group) || null, parentAccountId: null, clearParent: false, currencyCode: null,
          isControl: null, isBank: null, isMonetaryFx: null, allowDirectPosting: null, description: null,
        });
        return `${ex.id} saved`;
      }
      await h("POST", "/api/ledger/accounts", {
        groupId: n(v.group), accountCode: s(v.code).trim(), accountName: s(v.name), parentAccountId: null, currencyCode: null,
        isControl: false, isBank: false, isMonetaryFx: false, allowDirectPosting: true, description: null,
      });
      return `${s(v.code)} created`;
    },
    toggle: async (h, r, on) => {
      await h("POST", `/api/ledger/accounts/${r._id}/${on ? "activate" : "deactivate"}`, {});
      return `${s(r.name)} ${on ? "activated" : "deactivated"}`;
    },
  },

  AccountGroups: {
    title: "Account groups",
    subtitle: "How accounts are grouped on the trial balance and financial statements.",
    noun: "group",
    lookups: [],
    load: async (h) =>
      items(await h("GET", "/api/dashboard/ledger/groups")).map(
        (g) => ({ id: s(g.groupId), _id: n(g.groupId), name: s(g.name), type: s(g.type), label: `${s(g.type)} · ${s(g.name)}` } as Row)
      ),
    cols: [
      { key: "id", label: "No.", kind: "mono" },
      { key: "name", label: "Group" },
      { key: "type", label: "Type", kind: "status" },
    ],
    fields: [],
    filterKey: "type",
    filterLabel: "All types",
    tiles: (list) => [
      { label: "Groups", value: String(list.length), note: "Reference list" },
      { label: "Types", value: String(new Set(list.map((g) => g.type)).size), note: "Asset, liability, equity and more" },
    ],
  },

  Banks: {
    title: "Bank accounts",
    subtitle: "Bank and cash accounts. Balances come straight from the ledger.",
    noun: "bank account",
    lookups: ["accounts"],
    load: async (h) =>
      items(await h("GET", "/api/dashboard/lookups/banks")).map(
        (b) =>
          ({
            id: s(b.bankAccountId), _id: n(b.bankAccountId), name: s(b.name), bank: s(b.bank), number: s(b.accountNumber),
            gl: s(b.ledgerAccountCode), glId: n(b.ledgerAccountId), balance: n(b.balance), raw: b,
          } as Row)
      ),
    cols: [
      { key: "name", label: "Name" },
      { key: "bank", label: "Bank" },
      { key: "number", label: "Account number", kind: "mono" },
      { key: "gl", label: "Ledger account", get: (r, c) => { const a = c.accounts.find((x) => x.id === r.gl); return a ? `${a.id} · ${s(a.name)}` : s(r.gl); } },
      { key: "balance", label: "Balance", kind: "money", right: true },
    ],
    fields: [
      { key: "name", label: "Account name", kind: "text", required: true, full: true },
      { key: "bank", label: "Bank", kind: "text", required: true },
      { key: "number", label: "Account number", kind: "text" },
      {
        key: "gl", label: "Ledger account", kind: "select", required: true, full: true, lock: true, hint: "Cannot be changed later",
        options: (ctx) => ctx.accounts.filter((a) => n(a.isBank) === 1 && a.status === "Active").map((a) => ({ v: a.id, l: `${a.id} · ${s(a.name)}` })),
      },
    ],
    tiles: (list) => [
      { label: "Total cash", value: money(list.reduce((t, r) => t + n(r.balance), 0)), note: `${list.length} accounts` },
    ],
    defaults: () => ({}),
    toForm: (r) => ({ name: s(r.name), bank: s(r.bank), number: s(r.number), gl: s(r.gl) }),
    validate: (v, _c, ex, list) => (list.some((b) => b.gl === s(v.gl) && b.id !== ex?.id) ? "That ledger account already belongs to another bank account." : null),
    save: async (h, v, ctx, ex) => {
      const body = { ledgerAccountId: ex ? null : accountId(ctx, v.gl), accountName: s(v.name), bankName: s(v.bank), accountNumber: s(v.number) || null };
      if (ex) await h("PUT", `/api/dashboard/bank-accounts/${ex._id}`, body);
      else await h("POST", "/api/dashboard/bank-accounts", body);
      return `${s(v.name)} ${ex ? "saved" : "created"}`;
    },
    remove: async (h, r) => {
      await h("DELETE", `/api/dashboard/bank-accounts/${r._id}`);
      return `${s(r.name)} deleted`;
    },
  },

  Taxes: {
    title: "Tax rates",
    subtitle: "The tax codes offered on invoices and bills.",
    noun: "tax rate",
    lookups: ["accounts"],
    load: async (h) =>
      items(await h("GET", "/api/tax/codes", undefined, PAGE)).map(
        (t) =>
          ({
            id: s(t.code), _id: n(t.taxCodeId), name: s(t.description), type: s(t.taxType), rate: n(t.rate),
            status: t.isActive ? "Active" : "Inactive", raw: t,
          } as Row)
      ),
    cols: [
      { key: "id", label: "Code", kind: "mono" },
      { key: "name", label: "Description" },
      { key: "type", label: "Type", kind: "status" },
      { key: "rate", label: "Rate", kind: "pct", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true, lock: true, hint: "Cannot be changed later" },
      { key: "name", label: "Description", kind: "text", required: true },
      { key: "type", label: "Type", kind: "select", required: true, options: () => ["VAT", "WHT", "CIT", "PAYE", "Stamp Duty", "Other"].map((t) => ({ v: t, l: t })) },
      { key: "rate", label: "Rate (%)", kind: "number", required: true },
      { key: "account", label: "Tax account", kind: "select", full: true, lock: true, hint: "The account this tax posts to. Cannot be changed later", options: accountOpts("Liability", "Asset") },
    ],
    filterKey: "type",
    filterLabel: "All types",
    tiles: (list) => {
      const vat = list.find((t) => t.type === "VAT" && t.status === "Active");
      return [
        { label: "Tax codes", value: String(list.length), note: `${list.filter((r) => r.status === "Active").length} active` },
        { label: "VAT rate", value: vat ? `${n(vat.rate)}%` : "—", note: vat ? s(vat.name) : "No active VAT code" },
        { label: "Withholding", value: String(list.filter((r) => r.type === "WHT").length), note: "WHT codes" },
      ];
    },
    defaults: () => ({ type: "VAT" }),
    toForm: (r) => ({ code: r.id, name: s(r.name), type: s(r.type), rate: n(r.rate), account: "" }),
    validate: (v, _c, ex) =>
      n(v.rate) < 0 || n(v.rate) > 100 ? "Rate must be between 0 and 100." : !ex && !s(v.account) ? "Choose the account this tax posts to." : null,
    save: async (h, v, ctx, ex) => {
      if (ex) {
        await h("PUT", `/api/tax/codes/${ex._id}`, { ...rawOf(ex), description: s(v.name), taxType: s(v.type), rate: n(v.rate) });
        return `${ex.id} saved`;
      }
      const code = s(v.code).trim().toUpperCase();
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      await h("POST", "/api/tax/codes", {
        taxCodeId: 0, code, description: s(v.name), taxType: s(v.type), rate: n(v.rate), taxAccountId: accountId(ctx, v.account),
        isInclusive: false, effectiveFrom: today, effectiveTo: null, isActive: true,
      });
      return `${code} created`;
    },
    toggle: async (h, r, on) => {
      await h("PUT", `/api/tax/codes/${r._id}`, { ...rawOf(r), isActive: on });
      return `${s(r.name)} ${on ? "activated" : "deactivated"}`;
    },
  },

  Customers: partyEntity("customer"),
  Vendors: partyEntity("vendor"),

  AssetCategories: {
    title: "Asset categories",
    subtitle: "Useful life for each class of fixed asset. These drive the monthly depreciation run.",
    noun: "category",
    lookups: [],
    load: async (h) =>
      items(await h("GET", "/api/assets/categories", undefined, PAGE)).map(
        (c) => ({ id: s(c.categoryId), _id: n(c.categoryId), name: s(c.categoryName), months: n(c.usefulLifeMonths), years: Math.round((n(c.usefulLifeMonths) / 12) * 10) / 10 } as Row)
      ),
    cols: [
      { key: "id", label: "No.", kind: "mono" },
      { key: "name", label: "Category" },
      { key: "months", label: "Useful life (months)", kind: "num", right: true },
      { key: "years", label: "Useful life (years)", kind: "num", right: true },
    ],
    fields: [],
    tiles: (list) => [{ label: "Categories", value: String(list.length), note: "Reference list" }],
  },
};

/* ---------- display helpers ---------- */

const cell = (c: Col, r: Row, ctx: Ctx): Val => (c.get ? c.get(r, ctx) : r[c.key] ?? "");
const text = (c: Col, v: Val): string => {
  switch (c.kind) {
    case "money": return money(n(v));
    case "pct": return `${n(v)}%`;
    case "num": return n(v).toLocaleString("en-US");
    default: return s(v);
  }
};

/* ---------- record dialog ---------- */

const RecordDialog: React.FC<{
  title: string;
  submit: string;
  fields: Fld[];
  ctx: Ctx;
  initial: Record<string, Val>;
  existing: boolean;
  onClose: () => void;
  onSave: (v: Record<string, Val>) => Promise<string | null>;
}> = ({ title, submit, fields, ctx, initial, existing, onClose, onSave }) => {
  const [v, setV] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {};
    fields.forEach((f) => (o[f.key] = initial[f.key] !== undefined ? String(initial[f.key]) : ""));
    return o;
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    if (busy) return;
    const missing = fields.find((f) => f.required && !(v[f.key] ?? "").trim());
    if (missing) return setErr(`${missing.label} is required.`);
    const out: Record<string, Val> = {};
    fields.forEach((f) => {
      const raw = (v[f.key] ?? "").trim();
      out[f.key] = f.kind === "number" ? Number(raw) || 0 : raw;
    });
    setBusy(true);
    setErr(null);
    const bad = await onSave(out);
    setBusy(false);
    if (bad) setErr(bad);
  };

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={styles.accDialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {title}
          </DialogTitle>
          <DialogContent>
            <div className={styles.accForm}>
              {fields.map((f) => {
                const options = f.options ? f.options(ctx) : [];
                const locked = !!f.lock && existing;
                return (
                  <div key={f.key} className={f.full ? styles.accFormFull : undefined}>
                    <Field
                      label={f.label}
                      required={!!f.required}
                      hint={f.hint && !locked ? f.hint : undefined}
                    >
                      {f.kind === "select" ? (
                        <Select disabled={locked} value={v[f.key] ?? ""} onChange={(_, d) => setV({ ...v, [f.key]: d.value })}>
                          <option value="">Select…</option>
                          {options.map((o) => (
                            <option key={o.v} value={o.v}>{o.l}</option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          disabled={locked}
                          type={f.kind === "number" ? "number" : "text"}
                          value={v[f.key] ?? ""}
                          onChange={(_, d) => setV({ ...v, [f.key]: d.value })}
                        />
                      )}
                    </Field>
                  </div>
                );
              })}
            </div>
            {err && <div className={styles.accDlgErr} role="alert">{err}</div>}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button appearance="primary" disabled={busy} onClick={go}>
              {busy ? "Working…" : submit}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* ---------- the page ---------- */

const SettupAccounting: React.FC<SettupAccountingProps> = ({ view, token, baseUrl = "https://acc.cilpron.com" }) => {
  const ent = ENTITIES[view];
  const http = useMemo(() => makeHttp(baseUrl, token), [baseUrl, token]);

  const toasterId = useId("accounting-setup-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const notify = useCallback(
    (message: string, intent: Intent = "success") =>
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent, position: "top-end", timeout: 4000 }
      ),
    [dispatchToast]
  );

  const [rows, setRows] = useState<Row[]>([]);
  const [ctx, setCtx] = useState<Ctx>({ accounts: [], groups: [] });
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);

  const [form, setForm] = useState<{ row: Row | null; init: Record<string, Val> } | null>(null);
  const [del, setDel] = useState<Row | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const canAdd = !!ent.save && ent.fields.length > 0;

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const next: Ctx = { accounts: [], groups: [] };
      if (ent.lookups.includes("accounts")) next.accounts = await ENTITIES.Accounts.load(http, next);
      if (ent.lookups.includes("groups")) next.groups = await ENTITIES.AccountGroups.load(http, next);
      // the Accounts screen needs its own rows as lookups too (for the duplicate-code check)
      const list = await ent.load(http, next);
      if (view === "Accounts") next.accounts = list;
      setCtx(next);
      setRows(list);
    } catch (e) {
      setLoadErr(errText(e));
    } finally {
      setLoading(false);
    }
  }, [ent, http, view]);

  useEffect(() => {
    setQ("");
    setFilter("All");
    setSort(null);
    setForm(null);
    setDel(null);
    reload();
  }, [reload]);

  const filterCol = ent.cols.find((c) => c.key === ent.filterKey);
  const choices = useMemo(
    () => (filterCol ? Array.from(new Set(rows.map((r) => s(cell(filterCol, r, ctx))))).filter(Boolean).sort() : []),
    [rows, filterCol, ctx]
  );

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (filterCol && filter !== "All" && s(cell(filterCol, r, ctx)) !== filter) return false;
      return !needle || ent.cols.some((c) => text(c, cell(c, r, ctx)).toLowerCase().includes(needle));
    });
    if (sort) {
      const col = ent.cols.find((c) => c.key === sort.key);
      if (col) {
        out = [...out].sort((a, b) => {
          const x = cell(col, a, ctx);
          const y = cell(col, b, ctx);
          return (typeof x === "number" && typeof y === "number" ? x - y : s(x).localeCompare(s(y))) * sort.dir;
        });
      }
    }
    return out;
  }, [rows, q, filter, sort, ent, filterCol, ctx]);

  const tiles = useMemo(() => ent.tiles(rows), [ent, rows]);

  const openNew = () => setForm({ row: null, init: ent.defaults ? ent.defaults() : {} });
  const openEdit = (r: Row) => setForm({ row: r, init: ent.toForm ? ent.toForm(r, ctx) : {} });

  const submit = async (values: Record<string, Val>): Promise<string | null> => {
    if (!ent.save) return null;
    const bad = ent.validate ? ent.validate(values, ctx, form?.row ?? null, rows) : null;
    if (bad) return bad;
    try {
      const message = await ent.save(http, values, ctx, form?.row ?? null, rows);
      setForm(null);
      notify(message);
      await reload();
      return null;
    } catch (e) {
      return errText(e);
    }
  };

  const flip = async (r: Row) => {
    if (!ent.toggle || busyId) return;
    setBusyId(r.id);
    try {
      notify(await ent.toggle(http, r, r.status !== "Active"));
      await reload();
    } catch (e) {
      notify(errText(e), "error");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!del || !ent.remove) return;
    setBusyId(del.id);
    try {
      notify(await ent.remove(http, del));
      setDel(null);
      await reload();
    } catch (e) {
      notify(errText(e), "error");
      setDel(null);
    } finally {
      setBusyId(null);
    }
  };

  const hasActions = !!(ent.save || ent.toggle || ent.remove);

  return (
    <div className={styles.accWrap}>
      <Toaster toasterId={toasterId} />

      <header className={styles.accHead}>
        <div>
          <h2 className={styles.accTitle}>{ent.title}</h2>
          <p className={styles.accSub}>{ent.subtitle}</p>
        </div>
        {canAdd && (
          <Button appearance="primary" icon={<Add20Regular />} onClick={openNew} disabled={loading && rows.length === 0}>
            New {ent.noun}
          </Button>
        )}
      </header>

      {loadErr && (
        <div className={styles.accErr} role="alert">
          {loadErr}
          <Button size="small" appearance="subtle" onClick={reload}>Try again</Button>
        </div>
      )}

      <div className={styles.accTiles}>
        {loading && rows.length === 0
          ? [0, 1, 2].map((i) => (
              <div key={i} className={styles.accTile}>
                <span className={styles.accSk} style={{ width: "50%", height: 11 }} />
                <span className={styles.accSk} style={{ width: "70%", height: 24, marginTop: 10 }} />
              </div>
            ))
          : tiles.map((t) => (
              <div key={t.label} className={styles.accTile}>
                <span className={styles.accTileLabel}>{t.label}</span>
                <span className={styles.accTileValue}>{t.value}</span>
                {t.note && <span className={styles.accTileNote}>{t.note}</span>}
              </div>
            ))}
      </div>

      <section className={styles.accCard}>
        <div className={styles.accToolbar}>
          <div className={styles.accSearch}>
            <Search20Regular />
            <input
              className={styles.accSearchInput}
              placeholder={`Search ${ent.title.toLowerCase()}`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search"
            />
          </div>
          {filterCol && (
            <select className={styles.accFilter} value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter">
              <option value="All">{ent.filterLabel ?? "All"}</option>
              {choices.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          <span className={styles.accSpacer} />
          <span className={styles.accCount}>{shown.length} of {rows.length}</span>
          <Button appearance="subtle" icon={<ArrowSync20Regular />} onClick={reload} disabled={loading} aria-label="Refresh" title="Refresh" />
        </div>

        <div className={styles.accTableWrap}>
          <table className={styles.accTable}>
            <thead>
              <tr>
                {ent.cols.map((c) => (
                  <th
                    key={c.key}
                    className={c.right ? styles.accRight : undefined}
                    onClick={() => setSort((p) => (p && p.key === c.key ? (p.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 }))}
                    aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}
                  >
                    {c.label}
                    <span className={styles.accSortMark}>{sort?.key === c.key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}</span>
                  </th>
                ))}
                {hasActions && <th className={styles.accRight} aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 &&
                [0, 1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    {ent.cols.map((c) => (
                      <td key={c.key}><span className={styles.accSk} style={{ width: "70%", height: 12 }} /></td>
                    ))}
                    {hasActions && <td />}
                  </tr>
                ))}
              {shown.map((r) => (
                <tr key={r.id} className={r.status === "Inactive" ? styles.accRowOff : undefined}>
                  {ent.cols.map((c) => {
                    const v = cell(c, r, ctx);
                    return (
                      <td key={c.key} className={`${c.right ? styles.accRight : ""} ${c.kind === "mono" ? styles.accMono : ""}`}>
                        {c.kind === "status" ? (v ? <Pill text={s(v)} /> : null) : text(c, v)}
                      </td>
                    );
                  })}
                  {hasActions && (
                    <td className={styles.accRight}>
                      <span className={styles.accActions}>
                        {ent.save && ent.fields.length > 0 && (
                          <button className={styles.accIconBtn} title="Edit" aria-label={`Edit ${r.id}`} onClick={() => openEdit(r)}>
                            <Edit20Regular />
                          </button>
                        )}
                        {ent.toggle && (
                          <button
                            className={styles.accIconBtn}
                            title={r.status === "Active" ? "Deactivate" : "Activate"}
                            aria-label={r.status === "Active" ? `Deactivate ${r.id}` : `Activate ${r.id}`}
                            disabled={busyId === r.id}
                            onClick={() => flip(r)}
                          >
                            {r.status === "Active" ? <Dismiss20Regular /> : <Checkmark20Regular />}
                          </button>
                        )}
                        {ent.remove && (
                          <button className={`${styles.accIconBtn} ${styles.accIconDanger}`} title="Delete" aria-label={`Delete ${r.id}`} onClick={() => setDel(r)}>
                            <Delete20Regular />
                          </button>
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && shown.length === 0 && !loadErr && (
            <div className={styles.accEmpty}>
              {rows.length === 0 ? `No ${ent.noun}s yet.` : "Nothing matches your search."}
              {rows.length === 0 && canAdd && (
                <Button appearance="primary" size="small" icon={<Add20Regular />} onClick={openNew}>
                  New {ent.noun}
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {form && (
        <RecordDialog
          title={form.row ? `Edit ${ent.noun}` : `New ${ent.noun}`}
          submit={form.row ? "Save changes" : "Create"}
          fields={ent.fields}
          ctx={ctx}
          initial={form.init}
          existing={!!form.row}
          onClose={() => setForm(null)}
          onSave={submit}
        />
      )}

      {del && (
        <Dialog open onOpenChange={(_, d) => !d.open && setDel(null)}>
          <DialogSurface className={styles.accDialog}>
            <DialogBody>
              <DialogTitle>Delete {ent.noun}?</DialogTitle>
              <DialogContent>
                <b>{s(del.name)}</b> will be removed. This can't be undone.
              </DialogContent>
              <DialogActions>
                <DialogTrigger action="close">
                  <Button>Cancel</Button>
                </DialogTrigger>
                <Button appearance="primary" className={styles.accDangerBtn} disabled={busyId === del.id} onClick={confirmDelete}>
                  Delete
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
};

export default SettupAccounting;